import { TextInput } from '@ardoq/forms';
import { DEFAULT_OPERATOR_TO_LABEL } from '@ardoq/query-builder';
import { Select } from '@ardoq/select';
import { Tag, TagGroup } from '@ardoq/status-ui';
import { UnknownColumnFilterInputProps } from '../types';
import { RemoveFilterButtonInTopRightCorner, SelectContainer } from './atoms';
import type { UnknownColumnFilterInputLabels } from '../labels';

export type { UnknownColumnFilterInputLabels };

const DEFAULT_LABELS = {
  fromPrefix: 'From',
  toPrefix: 'To',
  errorMessage:
    'The column may have been removed or changed in the original report. Remove the filter to fix.',
} satisfies Required<UnknownColumnFilterInputLabels>;

const DEFAULT_REMOVE_FILTER_BUTTON_LABEL = 'Remove filter';

const ValueDisplay = ({
  value,
  labels,
}: {
  value: UnknownColumnFilterInputProps['value'];
  labels: Required<UnknownColumnFilterInputLabels>;
}) => {
  if (value === undefined) return null;

  if (typeof value === 'object' && 'start' in value && 'end' in value) {
    return (
      <>
        <TextInput value={value.start} prefix={labels.fromPrefix} isReadOnly />
        <TextInput value={value.end} prefix={labels.toPrefix} isReadOnly />
      </>
    );
  }

  const tags = Array.isArray(value) ? value : [value];
  if (tags.length === 0) return null;

  return (
    <TagGroup>
      {tags.map((v, index) => (
        <Tag key={index} label={v} />
      ))}
    </TagGroup>
  );
};

export const UnknownColumnFilterInput = ({
  column,
  operator,
  removeFilter,
  value,
  isReadOnly,
  labels,
  removeFilterButtonLabel = DEFAULT_REMOVE_FILTER_BUTTON_LABEL,
}: UnknownColumnFilterInputProps & {
  labels?: UnknownColumnFilterInputLabels;
  removeFilterButtonLabel?: string;
}) => {
  const mergedLabels = { ...DEFAULT_LABELS, ...labels };
  return (
    <SelectContainer>
      {!isReadOnly && (
        <RemoveFilterButtonInTopRightCorner
          onClick={() => removeFilter(column.key)}
          label={removeFilterButtonLabel}
        />
      )}
      {
        <Select
          label={column.label}
          options={[
            {
              label: operator
                ? (DEFAULT_OPERATOR_TO_LABEL[operator] ?? operator)
                : '',
              value: operator,
            },
          ]}
          value={operator}
          errorMessage={mergedLabels.errorMessage}
          isReadOnly
        />
      }
      {value && <ValueDisplay value={value} labels={mergedLabels} />}
    </SelectContainer>
  );
};
